import React, { useEffect, useState, useCallback, memo } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { Container, Button, Typography } from "@material-ui/core";

import { getMyBrands } from "modules/brand";
import { addToCart as addToCartAction } from "modules/cart";
import LoadingIndicator from "components/LoadingIndicator";
import Brand from "components/brandListItem";
import { selectMyBrands, selectMyBrandsIsPending } from "selectors/brand";
import { selectCart } from "selectors/cart";
import { selectUser } from "selectors/user";
import makeStyles from "@material-ui/core/styles/makeStyles";

import { getBrandRenewalLink } from "util/APIUtils";
import AutorenewIcon from "@material-ui/icons/Autorenew";

const useStyles = makeStyles((theme) => ({
  nobrands: {
    padding: "2em",
  },
  renewalLinkButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
}));

const MyBrand = memo(({ brand }) => {
  const dispatch = useDispatch();
  const cart = useSelector(selectCart);
  const user = useSelector(selectUser);
  const history = useHistory();
  const addToCart = (...args) => dispatch(addToCartAction(...args));

  const classes = useStyles();
  const [renewalLink, setRenewalLink] = useState(null);
  const [showRenewalLink, setShowRenewalLink] = useState(
    brand?.status !== "EXPIRED"
  );

  useEffect(() => {
    const fetchRenewalLink = async () => {
      const { brandHash, pinHash } = await getBrandRenewalLink(
        user.id,
        brand.id
      );
      if (brandHash && pinHash) {
        setRenewalLink(`/${brandHash}/${pinHash}`);
      }
    };
    fetchRenewalLink();
  }, [user.id, brand.id]);

  useEffect(() => {
    if (brand?.status === "EXPIRED") {
      setShowRenewalLink(false);
    } else {
      setShowRenewalLink(true);
    }
  }, [brand?.status]);

  const showDetails = () => {
    history.push(`/brands/${brand.id}`);
  };

  const renewBrand = () => {
    if (renewalLink) {
      history.push(renewalLink);
    }
  };

  return (
    <div>
      <Brand
        key={brand.id}
        user={user}
        brand={brand}
        cart={cart}
        addToCart={addToCart}
      >
        <Button
          size="small"
          variant="outlined"
          color="default"
          onClick={showDetails}
        >
          Details
        </Button>
        {renewalLink && showRenewalLink && (
          <div>
            <Button
              className={classes.renewalLinkButton}
              size="medium"
              variant="contained"
              color="default"
              startIcon={<AutorenewIcon />}
              onClick={renewBrand}
            >
              Renew
            </Button>
          </div>
        )}
      </Brand>
    </div>
  );
});

MyBrand.propTypes = {
  brand: PropTypes.object.isRequired,
};

const MyBrands = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const brands = useSelector(selectMyBrands);
  const pending = useSelector(selectMyBrandsIsPending);
  const user = useSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const hasBrands = brands.length > 0;

  const getBrands = useCallback(async () => {
    await dispatch(getMyBrands());
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    getBrands();
  }, [getBrands]);

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <div className="page">
      {!user && (
        <center>
          <Typography className={classes.nobrands}>
            You are not currently logged in.
            <br />
            To see your brands, <Link to="/auth/login">login</Link> or{" "}
            <Link to="/auth/signup">create an account</Link>
            <br />
            if you have not already done so.
            <br />
          </Typography>
        </center>
      )}
      {user && !pending && !hasBrands && (
        <Typography className={classes.nobrands}>
          There are no brands currently linked to your account
        </Typography>
      )}
      <Container style={{ padding: "0" }} maxWidth="md">
        {brands.map((brand) => {
          return <MyBrand key={brand.id} brand={brand} />;
        })}
      </Container>
    </div>
  );
};

export default memo(MyBrands);
